import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FlexDirection,
  Image as CitrusImage,
  JustifyContent,
  withCitrusTheme,
  Heading6,
  Body2,
  Overflow,
  AlignSelf,
} from '@gousto-internal/citrus-react'

import { getProductImage } from './BasketUpsell/image'
import type { BasketUpsellProduct, BasketUpsellProductActionSource } from './BasketUpsell/types'
import { activateOnKeyboard } from './keyboard'
import { QuantityControl } from './QuantityControl'

const ProductImage = withCitrusTheme(CitrusImage, () => ({
  display: 'block',
  objectFit: 'cover',
  objectPosition: 'center',
  borderRadius: '0',
}))

type ProductRowProps = {
  product: BasketUpsellProduct
  onClickProduct: (product: BasketUpsellProduct) => void
  onAddProduct: (productId: string, source: BasketUpsellProductActionSource) => void
  onRemoveProduct: (productId: string, source: BasketUpsellProductActionSource) => void
}

export const ProductItem = ({
  product,
  onAddProduct,
  onClickProduct,
  onRemoveProduct,
}: ProductRowProps) => {
  const image = getProductImage(product)
  const isUnavailable = product.outOfStock || !product.isAvailable || !!product.limitReached
  const handleProductClick = () => onClickProduct(product)
  const handleProductKeyDown = (event: React.KeyboardEvent) =>
    activateOnKeyboard(event, handleProductClick)

  return (
    <Box
      key={product.id}
      display={Display.Flex}
      justifyContent={JustifyContent.FlexStart}
      alignItems={AlignItems.Center}
      gap={3}
      bg={Color.White}
      borderRadius={1.5}
      boxShadow="0 1px 6px rgba(0, 0, 0, 0.12)"
      paddingRight={2}
      color={Color.ColdGrey_900}
      data-testid="product"
      minHeight="92px"
      style={{ cursor: 'pointer', borderRadius: '6px' }}
      onClick={handleProductClick}
      onKeyDown={handleProductKeyDown}
      role="button"
      tabIndex={0}
    >
      <Box
        display={Display.Flex}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.Center}
        minHeight="92px"
        width="92px"
        minWidth="92px"
        alignSelf={AlignSelf.Stretch}
        overflowX={Overflow.Hidden}
        overflowY={Overflow.Hidden}
        style={{ borderRadius: '6px 0 0 6px' }}
      >
        {image.src ? (
          <ProductImage
            alt={image.alt}
            loading="lazy"
            sizes="92px"
            src={image.src}
            srcSet={image.srcSet}
            height="100%"
            width="92px"
          />
        ) : (
          <Box bg={Color.ColdGrey_50} height="100%" width="92px" />
        )}
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.FlexStart}
        flexGrow={1}
        flexShrink={1}
        height="100%"
        minWidth="0"
        paddingV={2}
      >
        <Heading6>{product.title}</Heading6>
        <Box
          display={Display.Flex}
          gap={1}
          color={Color.ColdGrey_600}
          style={{ whiteSpace: 'nowrap' }}
        >
          <Body2 color={Color.Inherit}>
            {product.listPrice}
            {product.portion?.label && ` • ${product.portion.label}`}
          </Body2>
        </Box>
      </Box>
      <Box
        style={{
          flexShrink: 0,
          width: '84px',
        }}
      >
        <QuantityControl
          count={product.qty}
          decrementIcon={product.qty > 1 ? 'remove' : 'delete_outline'}
          decrementLabel={product.qty > 1 ? 'Remove item' : 'Delete item'}
          onRemove={() => onRemoveProduct(product.id, 'basket')}
          onAdd={() => onAddProduct(product.id, 'basket')}
          addDisabled={isUnavailable}
        />
      </Box>
    </Box>
  )
}
